import React, {useMemo} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import PropTypes from 'prop-types';
import {debounce} from 'throttle-debounce';
import AsyncSelect from 'react-select/lib/Async';
import exportMessages from '../../intl/common/exportMessages';
import {DEFAULT_DEBOUNCE_TIMEOUT} from '../../constants/validationConstants';
import {sparePartsWebsocketCommands} from '../../constants/sparePartsWebsocketCommands';
import {claimsWebsocketCommands} from '../../constants/claimsWebsocketCommands';
import {techDocWebsocketCommands} from '../../constants/techDocWebsocketCommands';

const mapDealerToSelectionOption = (dealer) => {
    return {
        ...dealer,
        value: dealer.dealerNumber,
        label: `${dealer.dealerName} (${dealer.dealerNumber})`
    };
};

// Generic search function factory
const createSearchDealers = (websocketCommand, websocketFlag, stateSelector) => {
    return (searchTerm) => {
        return (dispatch, getState) => {
            return new Promise((resolve, reject) => {
                const correlationId = `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                
                let timeoutId;
                let intervalId;
                
                const checkForResults = () => {
                    const state = getState();
                    const { searchResults } = stateSelector(state);
                    
                    if (searchResults && searchResults.correlationId === correlationId) {
                        if (timeoutId) clearTimeout(timeoutId);
                        if (intervalId) clearInterval(intervalId);
                        resolve(searchResults.dealers || []);
                        return true;
                    }
                    return false;
                };
                
                dispatch({
                    type: websocketCommand,
                    [websocketFlag]: true,
                    command: websocketCommand,
                    payload: { searchTerm, correlationId }
                });
                
                intervalId = setInterval(() => {
                    if (checkForResults()) {
                        return;
                    }
                }, 100);
                
                timeoutId = setTimeout(() => {
                    if (intervalId) clearInterval(intervalId);
                    reject(new Error('Search timeout'));
                }, 10000);
                
                checkForResults();
            });
        };
    };
};

// Export search functions for each module
export const searchSparePartDealers = createSearchDealers(
    sparePartsWebsocketCommands.SPARE_PARTS_ORDERS_DEALERS_GET,
    'sparePartsWebsocket',
    (state) => state.sparePartDealer
);

export const searchClaimsDealers = createSearchDealers(
    claimsWebsocketCommands.CLAIMS_DEALERS_GET,
    'claimsWebsocket',
    (state) => state.claimsDealers
);

export const searchTechDocDealers = createSearchDealers(
    techDocWebsocketCommands.TECH_DOC_DEALERS_GET,
    'techDocWebsocket',
    (state) => state.docExportDealers
);

const SelectDealer = props => {
    const {
        label, 
        placeholder, 
        noOptionsMessage, 
        handleDealerSelection, 
        intl: {formatMessage}, 
        defaultValue,
        searchDealers
    } = props;

    // Memoize the debounced search function to avoid creating new instances on every render
    const debouncedSearch = useMemo(
        () => debounce(DEFAULT_DEBOUNCE_TIMEOUT, (inputValue, callback) => {
            if (!inputValue || inputValue.trim() === '') {
                callback([]);
                return;
            }

            searchDealers(inputValue.trim())
                .then(dealers => {
                    const options = [...dealers]
                        .sort((dealer1, dealer2) => dealer1.dealerName.localeCompare(dealer2.dealerName))
                        .map(mapDealerToSelectionOption);
                    callback(options);
                })
                .catch(error => {
                    console.error('Dealer search failed:', error);
                    callback([]);
                });
        }),
        [searchDealers]
    );

    return (
        <div>
            {label && (
                <div className="row m-0">
                    <label className="col-form-label pl-0 font-weight-bold">
                        <FormattedMessage {...label}/>
                    </label>
                </div>
            )}
            <AsyncSelect 
                placeholder={placeholder ? formatMessage(placeholder) : formatMessage(exportMessages.DEALER_SEARCH_PLACEHOLDER)}
                noOptionsMessage={() => noOptionsMessage ? formatMessage(noOptionsMessage) : formatMessage(exportMessages.DEALER_SEARCH_NO_OPTIONS)}
                loadOptions={debouncedSearch}
                defaultValue={defaultValue}
                onChange={handleDealerSelection}
                isClearable
            />
        </div>
    );
};

SelectDealer.propTypes = {
    label: PropTypes.object,
    placeholder: PropTypes.object,
    noOptionsMessage: PropTypes.object,
    handleDealerSelection: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    defaultValue: PropTypes.object,
    searchDealers: PropTypes.func.isRequired
};

export default injectIntl(SelectDealer);
